import React from "react";
import { Flame, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

/**
 * Anonymous-only teaser that explains the daily streak system.
 * Renders nothing when a user is logged in (the real StreakWidget takes over).
 *
 * Goal: discoverability — make it obvious that signing in unlocks a daily
 * gamified reward loop that pays out real discount codes.
 */
export default function StreakTeaser({ onSignIn }) {
  const { user } = useAuth();
  if (user) return null;

  const milestones = [
    { day: 7, reward: "10% OFF" },
    { day: 14, reward: "15% OFF" },
    { day: 30, reward: "25% OFF" },
    { day: 100, reward: "LIFETIME VIP" },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="streak-teaser">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#151A14] to-[#0A0E0B] border-2 border-[#DD5422] p-6 md:p-10">
        <div className="absolute -right-12 -top-12 opacity-10">
          <Flame className="w-60 h-60 text-[#DD5422]" strokeWidth={1} fill="#DD5422" />
        </div>
        <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-3 flex items-center gap-2">
              <Flame className="w-4 h-4" /> / Show Up Daily
            </div>
            <h2 className="font-anton text-4xl md:text-5xl uppercase tracking-tight leading-none">
              Visit Every Day,<br /><span className="text-[#DD5422]">Earn Real Discounts.</span>
            </h2>
            <p className="text-[#8D9688] mt-4 max-w-md">
              Sign in and tap your daily check-in. Hit 7 days — score 10% off. Hit 30 — score 25%. Keep showin' up, keep stackin' rewards.
            </p>
            <Button
              data-testid="streak-teaser-signin"
              onClick={onSignIn}
              className="mt-6 bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest h-12 px-6 border-2 border-[#DD5422]"
            >
              Start My Streak <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {milestones.map((m) => (
              <div key={m.day} className="border-2 border-[#2C332A] bg-[#0A0E0B]/60 p-4 flex items-center gap-3" data-testid={`streak-teaser-milestone-${m.day}`}>
                <div className="relative shrink-0">
                  <Flame className="w-10 h-10 text-[#DD5422]" strokeWidth={1.5} fill="#DD5422" fillOpacity={0.2} />
                  <div className="absolute inset-0 flex items-center justify-center font-anton text-xs text-[#0A0E0B]">{m.day}</div>
                </div>
                <div>
                  <div className="font-anton text-base uppercase leading-none">{m.reward}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8D9688] mt-0.5">at day {m.day}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
